import { c as _c } from "react/compiler-runtime";
import Avatar from '@precis-digital/kurama/Avatar';
import Button from '@precis-digital/kurama/Button';
import CopyIcon from '@precis-digital/kurama/icons/CopyIcon';
import Divider from '@precis-digital/kurama/Divider';
import DownloadIcon from '@precis-digital/kurama/icons/DownloadIcon';
import Dropdown, { DropdownItem, DropdownList, DropdownListSubCategory } from '@precis-digital/kurama/Dropdown';
import Grid from '@precis-digital/kurama/Grid';
import MaximizeIcon from '@precis-digital/kurama/icons/MaximizeIcon';
import MinimizeIcon from '@precis-digital/kurama/icons/MinimizeIcon';
import MoreIcon from '@precis-digital/kurama/icons/MoreIcon';
import ReloadIcon from '@precis-digital/kurama/icons/ReloadIcon';
import { type Theme } from '@precis-digital/kurama/theme';
import Toggle from '@precis-digital/kurama/Toggle';
import Typography from '@precis-digital/kurama/Typography';
import { useTheme } from '@precis-digital/kurama/utils';
import React, { type ReactElement, useState } from 'react';
import { ASSETS_BASE_URL } from 'shared/constants';
import type { ChartCardMoreMenuProps } from '../internalTypes';
import type { ChartCardAction, ChartCardActionContext, ChartDataRow } from '../types';
import { getActionClickHandler } from '../utils/card';
import { CARD_TYPE_TO_ICON } from '../constants';
const addActionsDropDownItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(dropDownItems: ReactElement[], moreMenuActions: Array<ChartCardAction<DataKey, DataRow>>, getContext: () => ChartCardActionContext<DataKey, DataRow> | null): void => {
  if (moreMenuActions.length > 0) {
    dropDownItems.push(<Divider />);
  }
  for (const {
    id,
    label,
    icon,
    onClick,
    Component
  } of moreMenuActions) {
    if (Component == null) {
      dropDownItems.push(<DropdownItem key={id} onClick={getActionClickHandler(id, onClick, getContext)} leftIcon={icon}>
          {icon == null ? label ?? id : label}
        </DropdownItem>);
    } else {
      dropDownItems.push(<Component key={id} id={id} getContext={getContext} />);
    }
  }
};
const addExportDropDownItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(props: ChartCardMoreMenuProps<DataKey, DataRow>, handleClose: () => void, dropDownItems: ReactElement[]): void => {
  const {
    view,
    allowExport,
    handleExport,
    t
  } = props;
  if (allowExport !== true) {
    return;
  }
  if (view === 'table') {
    dropDownItems.push(<DropdownListSubCategory key="export_as" header={t('exportAs')}>
        <DropdownItem key="export_csv" onClick={() => {
        handleExport('csv');
        handleClose();
      }} leftIcon={<DownloadIcon />}>
          {t('exportAsCSV')}
        </DropdownItem>
        <DropdownItem key="export_google_sheets" onClick={() => {
        handleExport('googleSheets');
        handleClose();
      }} leftIcon={<Avatar kind="image" imageUrl={`${ASSETS_BASE_URL}/google_sheets.svg`} size="small" />}>
          {t('exportAsGoogleSheets')}
        </DropdownItem>
      </DropdownListSubCategory>);
    return;
  }
  dropDownItems.push(<DropdownListSubCategory key="export_as" header={t('exportAs')}>
      <DropdownItem key="export_svg" onClick={() => {
      void handleExport('svg', false);
      handleClose();
    }} leftIcon={<DownloadIcon />}>
        {t('exportAsSVG')}
      </DropdownItem>
      <DropdownItem key="copy_svg" onClick={() => {
      void handleExport('svg', true);
      handleClose();
    }} leftIcon={<CopyIcon />}>
        {t('copyAsSVG')}
      </DropdownItem>
      <DropdownItem key="export_png" onClick={() => {
      void handleExport('png', false);
      handleClose();
    }} leftIcon={<DownloadIcon />}>
        {t('exportAsPNG')}
      </DropdownItem>
      <DropdownItem key="copy_png" onClick={() => {
      void handleExport('png', true);
      handleClose();
    }} leftIcon={<CopyIcon />}>
        {t('copyAsPNG')}
      </DropdownItem>
    </DropdownListSubCategory>);
};
const addChangeCardTypeDropDownItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(props: ChartCardMoreMenuProps<DataKey, DataRow>, handleClose: () => void, dropDownItems: ReactElement[]): void => {
  const {
    otherCardTypes,
    changeCardType,
    t
  } = props;
  if (otherCardTypes.length === 0) {
    return;
  }
  dropDownItems.push(<Divider />);
  for (const changeTo of otherCardTypes) {
    const Icon = CARD_TYPE_TO_ICON[changeTo];
    dropDownItems.push(<DropdownItem key="change_card_type" onClick={() => {
      changeCardType(changeTo);
      handleClose();
    }} leftIcon={<Icon />}>
        {t('changeToCardType', {
        cardType: changeTo
      })}
      </DropdownItem>);
  }
};
const getDropDownItems = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(theme: Theme, props: ChartCardMoreMenuProps<DataKey, DataRow>, handleClose: () => void): ReactElement[] => {
  const {
    view,
    moreMenuActions,
    expandedToggle,
    allowResetCard,
    handleResetCard,
    needsResetCard,
    getContext,
    t
  } = props;
  const dropDownItems: ReactElement[] = [];
  if (expandedToggle != null) {
    dropDownItems.push(<DropdownItem key="expand_collapse" onClick={() => {
      expandedToggle.toggle();
      handleClose();
    }} leftIcon={expandedToggle.expanded ? <MinimizeIcon /> : <MaximizeIcon />}>
        {expandedToggle.expanded ? t('collapse') : t('expand')}
      </DropdownItem>);
  }
  if (allowResetCard === true) {
    dropDownItems.push(<DropdownItem key="reset_selections" onClick={() => {
      handleResetCard();
      handleClose();
    }} disabled={!needsResetCard} leftIcon={<ReloadIcon />}>
        {view === 'table' ? t('resetTable') : t('resetChart')}
      </DropdownItem>);
  }
  if (view === 'cartesian' && props.allowZooming) {
    dropDownItems.push(<DropdownItem key="reset_zooming" onClick={() => {
      props.handleResetZooming();
      handleClose();
    }} disabled={props.zoomedIn === false}>
        {t('resetZooming')}
      </DropdownItem>);
  }
  addExportDropDownItems(props, handleClose, dropDownItems);
  if (view === 'table' && props.allowToggleUsePagination === true) {
    dropDownItems.push(<Divider />, <DropdownItem key="pagination">
        <Grid display="inline-flex" justifyContent="space-between" gap={theme.spacing(3)} alignItems="center" width="100%" onClick={() => {
        props.toggleUsePagination();
        handleClose();
      }}>
          <Typography key="text" variant="body1">
            {t('pagination')}
          </Typography>
          <Toggle key="toggle" aria-label={t('colorScale')} name="colorScale" checked={props.usePagination} defaultChecked />
        </Grid>
      </DropdownItem>);
  }
  addChangeCardTypeDropDownItems(props, handleClose, dropDownItems);
  if (moreMenuActions != null) {
    addActionsDropDownItems(dropDownItems, moreMenuActions, getContext);
  }
  return dropDownItems;
};
const ChartCardMoreMenu = props => {
  const $ = _c(12);
  const [dropdownAnchorEl, setDropdownAnchorEl] = useState(null);
  const theme = useTheme();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = event => {
      event.stopPropagation();
      setDropdownAnchorEl(event.currentTarget);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const handleClick = t0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setDropdownAnchorEl(null);
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleClose = t1;
  let t2;
  if ($[2] !== props || $[3] !== theme) {
    t2 = getDropDownItems(theme, props, handleClose);
    $[2] = props;
    $[3] = theme;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const dropDownItems = t2;
  if (dropDownItems.length === 0) {
    return null;
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Button onClick={handleClick} variant="text" leftIcon={<MoreIcon />} />;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const t4 = dropdownAnchorEl != null;
  let t5;
  if ($[6] !== dropDownItems) {
    t5 = <DropdownList minWidth="300px">{dropDownItems}</DropdownList>;
    $[6] = dropDownItems;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== dropdownAnchorEl || $[9] !== t4 || $[10] !== t5) {
    t6 = <>{t3}<Dropdown anchorEl={dropdownAnchorEl} open={t4} onClose={handleClose}>{t5}</Dropdown></>;
    $[8] = dropdownAnchorEl;
    $[9] = t4;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
};
export default ChartCardMoreMenu;